import { Metric } from '@strength/domain';
import {
  BenchmarkData,
  getInputDisplay,
  getMetricSpec,
} from '@strength/domain-workout';
import { useMemo } from 'react';
import { useWorkoutContext } from '../../context/WorkoutContext';
import { BaseNumericInput } from './BaseNumericInput';
import { DeltaIndicator } from './DeltaIndicator';
import { BaseMetricInputProps } from './types';

export const WeightInput = ({
  workoutSetId,
  setMetric,
  exerciseMetric,
  originalSetMetric,
  libraryExerciseId,
  onEditSetMetric,
  disabled,
}: {
  libraryExerciseId: string;
  disabled?: boolean;
} & BaseMetricInputProps) => {
  // Per the athlete's team (resolved in WorkoutProvider), not a single org/team unit.
  const {
    getMetric,
    getUnitsForMetric,
    defaultWeightUnit,
    selectedAthlete,
    applyBenchmarkData,
  } = useWorkoutContext();

  // Threading the full metric (not a bare `{ type: 'intensity' }`) is what lets
  // getInputDisplay/getMetricSpec recognize a configured keyword (e.g. "Max").
  const metric = getMetric(exerciseMetric.metricId);

  const benchmarkData = {
    libraryExerciseId,
    profileId: selectedAthlete.id,
    benchmarkGridId: exerciseMetric.benchmarkGridId,
    applyBenchmarkData,
  };

  const unitOptions = useMemo(() => {
    return getUnitsForMetric(exerciseMetric.metricId, true);
  }, [exerciseMetric.metricId, getUnitsForMetric]);

  return (
    <BaseNumericInput
      display={getInputDisplay({
        metric,
        prescribedValue: originalSetMetric?.input,
        actualValue: setMetric?.input,
        benchmarkData,
        defaultWeightUnit,
      })}
      workoutSetId={workoutSetId}
      metricId={exerciseMetric.metricId}
      onEditSetMetric={onEditSetMetric}
      disabled={disabled}
      endAdornment={
        <WeightDeltaIndicator
          metric={metric}
          prescribed={originalSetMetric?.input}
          actual={setMetric?.input}
          benchmarkData={benchmarkData}
        />
      }
      units={unitOptions}
    />
  );
};

const WeightDeltaIndicator = ({
  metric,
  prescribed,
  actual,
  benchmarkData,
}: {
  metric: Metric;
  prescribed?: string;
  actual?: string;
  benchmarkData?: BenchmarkData;
}) => {
  const spec = getMetricSpec(metric);
  const parsed = spec.schema.safeParse(prescribed);
  const prescribedPercentage =
    parsed.data?.format === 'percentage' && !benchmarkData;

  if (prescribedPercentage) return null;

  return (
    <DeltaIndicator
      metric={metric}
      prescribed={prescribed}
      actual={actual}
      benchmarkData={benchmarkData}
    />
  );
};
